export const liveCodingQuestions = [
  {
    id: 1,
    question: "Implement a debounce function",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "function debounce(func, delay) {\n  let timeoutId;\n  return function(...args) {\n    clearTimeout(timeoutId);\n    timeoutId = setTimeout(() => {\n      func.apply(this, args);\n    }, delay);\n  };\n}\n\n// Usage:\nconst searchInput = debounce((query) => {\n  console.log('Searching for:', query);\n}, 500);\n\nsearchInput('a');\nsearchInput('ab');\nsearchInput('abc'); // Only this executes after 500ms"
      },
      {
        type: "text",
        content: `**Debounce** delays execution until a period of inactivity.

**Use Cases**
- 🔎 Search input
- 📏 Window resize
- 🧭 Scroll events
- 🌐 API calls triggered by user input`
      }
    ]
  },
  {
    id: 2,
    question: "What will be logged to the console?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "const a = { c: 1 };\nconst b = { c: 1 };\n\nconsole.log(a === b);"
      },
      {
        type: "text",
        content: `**Answer:** \`false\`

**Explanation:**
- In JavaScript, objects are compared by **reference**, not by value
- Even though \`a\` and \`b\` have the same structure and values, they are **different objects** in memory
- The \`===\` operator checks if both variables point to the **same memory location**
- Since \`a\` and \`b\` are separate objects, the comparison returns \`false\``
      }
    ]
  },
  {
    id: 3,
    question: "What will be logged to the console?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "const a = {\n  b: 1,\n}\nconst c = (o) => {\n  delete o.b;\n  return o;\n}\nc(a);\nconsole.log(a.b);\n\nconst b = a;\nb.d = 2;\nconsole.log(a.d);"
      },
      {
        type: "text",
        content: `**Answer:** \`undefined\` then \`2\`

**Explanation:**
- \`console.log(a.b)\` outputs \`undefined\` because the property \`b\` was deleted from object \`a\` inside function \`c\`
- Objects are passed by **reference** in JavaScript, so when we pass \`a\` to function \`c\`, it modifies the original object
- \`const b = a\` creates a reference to the same object, not a copy
- When we add \`d: 2\` to \`b\`, we're actually modifying the original object that \`a\` points to
- Therefore \`console.log(a.d)\` outputs \`2\``
      }
    ]
  },
  {
    id: 4,
    question: "What will be logged to the console?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "console.log(f1);\nconsole.log(f2);\nfunction f1() {}\nvar f2 = function () {}"
      },
      {
        type: "text",
        content: `**Answer:** \`[Function: f1]\` then \`undefined\`

**Explanation:**
- This demonstrates **hoisting** in JavaScript
- **Function declarations** (\`function f1() {}\`) are hoisted with their entire definition
  - \`f1\` is available before its declaration line
- **Function expressions** (\`var f2 = function() {}\`) are only partially hoisted
  - The variable \`f2\` is hoisted but initialized as \`undefined\`
  - The function assignment happens at runtime when the code reaches that line
- Therefore, \`f1\` logs the function while \`f2\` logs \`undefined\``
      }
    ]
  },
  {
    id: 5,
    question: "What happens in memory with this large object? How does the browser/garbage collector handle it?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "window.myLargeObject = {\n  // large object 300MB\n  // what happens in memory what will the browser do / garbage collector etc.\n};"
      },
      {
        type: "text",
        content: `**Memory Implications:**

**1. Global Scope Memory**
- The object is assigned to \`window\`, making it a **global variable**
- It will **NOT be garbage collected** as long as the page is open
- The 300MB will remain in memory throughout the page lifecycle

**2. Browser Behavior**
- May trigger **memory warnings** if total memory usage is high
- On low-memory devices, browser might become **slow or unresponsive**
- In extreme cases, the browser may **crash or kill the tab**

**3. Garbage Collection**
- The garbage collector **cannot reclaim this memory** because:
  - The object has a reference from the global \`window\` object
  - Global variables persist for the lifetime of the page
- Only removed when: page is closed, navigated away, or manually set to \`null\`

**Best Practices:**
- ❌ Avoid storing large data in global scope
- ✅ Use local scope and let GC clean up when done
- ✅ Use \`myLargeObject = null\` when no longer needed
- ✅ Consider IndexedDB or localStorage for large data
- ✅ Use Web Workers for heavy computation with large data`
      }
    ]
  },
  {
    id: 6,
    question: "What will be logged to the console?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "class A {\n  constructor() {\n    this.b = 1;\n    this.c = function () {\n      console.log('b is ', this.b);\n    }\n  }\n}\nconst a = new A();\na.c();"
      },
      {
        type: "text",
        content: `**Answer:** \`b is 1\`

**Explanation:**
- When \`a.c()\` is called, the function is invoked as a **method** of object \`a\`
- Since \`c\` is a regular function (not an arrow function), \`this\` refers to the object it's called on
- In this case, \`this\` points to \`a\`, so \`this.b\` correctly accesses \`a.b\` which is \`1\`

**Important Note:**
- Defining methods in the constructor creates a **new function instance** for each object
- This can be memory-inefficient for many instances
- Better approach: Use class methods or arrow functions in the class body

\`\`\`javascript
// More efficient:
class A {
  constructor() {
    this.b = 1;
  }
  c() {
    console.log('b is ', this.b);
  }
}
\`\`\``
      }
    ]
  },
  {
    id: 7,
    question: "What will be the order of console logs?",
    parts: [
      {
        type: "code",
        language: "javascript",
        content: "console.log(1);\n\nprocess.nextTick(() => { console.log(2) });\n\nsetImmediate(() => { console.log(3) });\n\nPromise.resolve().then(() => { console.log(4) });\n\nconsole.log(5);\n\nsetTimeout(() => {\n  console.log(6);\n}, 0);\n\nconsole.log(7);"
      },
      {
        type: "text",
        content: `**Answer:** \`1, 5, 7, 2, 4, 6, 3\`

**Explanation:**

This demonstrates the **Node.js Event Loop** and task prioritization:

**1. Synchronous Code (Main Thread)**
- \`1\`, \`5\`, \`7\` - Execute immediately in order

**2. Microtasks (High Priority)**
- \`process.nextTick()\` - Executes **before** other microtasks (outputs \`2\`)
- \`Promise.resolve().then()\` - Microtask queue (outputs \`4\`)

**3. Macrotasks (Lower Priority)**
- \`setTimeout()\` - Timers phase (outputs \`6\`)
- \`setImmediate()\` - Check phase (outputs \`3\`)

**Event Loop Phases:**
1. ⚡ **Synchronous** code runs first
2. 🔄 **process.nextTick** queue (before microtasks)
3. 🎯 **Microtasks** (Promises, queueMicrotask)
4. ⏱️ **Timers** (setTimeout, setInterval)
5. ✅ **Check** (setImmediate)

**Key Points:**
- \`process.nextTick\` has the **highest priority** among async operations
- Microtasks execute **before** macrotasks
- \`setImmediate\` typically runs **after** \`setTimeout(..., 0)\` in the first iteration`
      }
    ]
  },
  {
    id: 8,
    question: "What will be logged to the console in each example?",
    parts: [
      {
        type: "text",
        content: "**Example 1: Using `let`**"
      },
      {
        type: "code",
        language: "javascript",
        content: "for(let i = 5; i < 8; i++) {\n  setTimeout(() => {\n    console.log(i);\n  })\n}\n\nconsole.log(i);"
      },
      {
        type: "text",
        content: `**Answer:** \`ReferenceError: i is not defined\`

Then after the setTimeout callbacks:
\`5\`, \`6\`, \`7\`

**Explanation:**
- \`console.log(i)\` **outside** the loop throws a **ReferenceError** because \`let\` is **block-scoped**
- The variable \`i\` only exists inside the for loop block
- The setTimeout callbacks log \`5\`, \`6\`, \`7\` because \`let\` creates a **new binding** for each iteration
- Each callback captures its own distinct \`i\` value`
      },
      {
        type: "text",
        content: "**Example 2: Using `var`**"
      },
      {
        type: "code",
        language: "javascript",
        content: "for(var i = 5; i < 8; i++) {\n  setTimeout(() => {\n    console.log(i);\n  })\n}"
      },
      {
        type: "text",
        content: `**Answer:** \`8\`, \`8\`, \`8\`

**Explanation:**
- With \`var\`, the variable \`i\` is **function-scoped** (or globally scoped if not in a function)
- There is only **ONE** \`i\` variable shared across all iterations
- By the time the setTimeout callbacks execute, the loop has already completed
- The final value of \`i\` is \`8\` (the value that made the loop condition \`i < 8\` false)
- All three callbacks reference the **same** \`i\`, which is \`8\`

**Key Difference:**
- 🔒 \`let\`: Block-scoped, creates a **new variable** for each iteration
- 🌐 \`var\`: Function-scoped, **one variable** shared across all iterations

**Fix for \`var\` (if you want 5, 6, 7):**
\`\`\`javascript
for(var i = 5; i < 8; i++) {
  (function(j) {
    setTimeout(() => {
      console.log(j);
    })
  })(i);
}
\`\`\``
      }
    ]
  }
];

